<template>
  <div class="page-container">
    <div class="page-title-container d-flex align-items-center justify-content-between">
      <h1 class="page-title mb-0">Importar viajes de Despegar</h1>
      <router-link :to="{ name: 'imports-despegar' }" class="button button-light">Volver</router-link>
    </div>

    <!-- ═══════════ 1. CARGA ═══════════ -->
    <b-card v-if="step === 'upload'" tag="article" no-body class="custom-card" style="max-width: 760px">
      <b-card-header header-tag="section" class="custom-card-header">
        <h3>Subir archivo</h3>
      </b-card-header>
      <b-card-body class="custom-card-body">
        <p class="text-muted mb-3">
          Sube el Excel descargado del portal de Despegar. Se procesarán solo las reservas emitidas.
          Un mismo archivo puede traer viajes de Lima y de Cusco.
        </p>

        <div
          class="drop-zone"
          :class="{ 'drop-zone--over': dragOver, 'drop-zone--ready': !!file }"
          @dragover.prevent="dragOver = true"
          @dragleave.prevent="dragOver = false"
          @drop.prevent="onDrop"
          @click="$refs.fileInput.click()"
        >
          <i class="ri-file-excel-2-line drop-zone__icon"></i>
          <template v-if="file">
            <div class="drop-zone__name">{{ file.name }}</div>
            <div class="drop-zone__hint">{{ fileSize }} · clic para cambiar</div>
          </template>
          <template v-else>
            <div class="drop-zone__name">Arrastra el archivo aquí</div>
            <div class="drop-zone__hint">o haz clic para seleccionarlo (.xlsx)</div>
          </template>
          <input ref="fileInput" type="file" accept=".xlsx" class="d-none" @change="onFileChange" />
        </div>

        <div class="d-flex justify-content-end mt-4">
          <b-button variant="none" class="button button-primary" :disabled="!file" @click="processFile">
            Procesar
          </b-button>
        </div>
      </b-card-body>
    </b-card>

    <!-- ═══════════ 2. PREVIEW ═══════════ -->
    <template v-if="step === 'preview' && preview">
      <div class="row mb-3">
        <div class="col-6 col-lg-3 mb-2" v-for="c in summaryCards" :key="c.key">
          <div class="summary-card" :class="c.key">
            <span class="summary-card__label">{{ c.label }}</span>
            <span class="summary-card__value">{{ c.value }}</span>
          </div>
        </div>
      </div>
      <p v-if="preview.saltadas" class="text-muted small">
        {{ preview.saltadas }} fila(s) del archivo no son reservas emitidas y no se procesaron.
      </p>

      <div class="table-container">
        <div class="table-header d-flex flex-wrap justify-content-between align-items-center">
          <div class="d-flex flex-wrap align-items-center">
            <button
              v-for="f in statusFilters"
              :key="f.key"
              type="button"
              class="status-pill mr-2 mb-1"
              :class="{ active: statusFilter === f.key }"
              @click="setStatusFilter(f.key)"
            >
              {{ f.label }} ({{ f.count }})
            </button>
          </div>
          <div class="d-flex flex-wrap align-items-center mt-2 mt-md-0">
            <label v-if="preview.errores" class="ignore-errors mr-3 mb-0">
              <input type="checkbox" v-model="ignoreErrors" />
              <span>Importar aunque haya errores</span>
            </label>
            <b-button variant="none" class="button button-light mr-2" @click="reset">Subir otro archivo</b-button>
            <b-button variant="none" class="button button-primary" :disabled="!canImport" @click="confirmImport">
              Importar {{ preview.nuevas }} viaje{{ preview.nuevas === 1 ? '' : 's' }} nuevo{{ preview.nuevas === 1 ? '' : 's' }}
            </b-button>
          </div>
        </div>

        <div class="table-responsive mb-0">
          <table class="custom-table w-100">
            <thead>
              <tr>
                <th class="pl-4">Fila</th>
                <th class="text-left">Estado</th>
                <th class="text-left">Ride</th>
                <th class="text-center">Fecha</th>
                <th class="text-center">Hora</th>
                <th class="text-left">Ruta</th>
                <th class="text-left">Pasajero</th>
                <th class="text-center">Vehículo</th>
                <th class="text-center">Precio</th>
                <th class="text-center">Ciudad</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in pagedRows" :key="r.row" :class="`row-${r.status.toLowerCase()}`">
                <td class="pl-4 text-muted">{{ r.row }}</td>
                <td class="status-cell">
                  <span :class="`badge ${statusMeta(r.status).color}`">{{ statusMeta(r.status).label }}</span>
                  <ul v-if="r.errores && r.errores.length" class="row-messages row-messages--error">
                    <li v-for="(e, i) in r.errores" :key="i">{{ e }}</li>
                  </ul>
                  <ul v-if="r.warnings && r.warnings.length" class="row-messages row-messages--warning">
                    <li v-for="(w, i) in r.warnings" :key="i"><i class="ri-error-warning-line"></i> {{ w }}</li>
                  </ul>
                  <div v-if="r.status === 'DUPLICADA'" class="row-messages">{{ duplicateReason(r) }}</div>
                </td>
                <td class="ride-code">{{ r.ride_code || '—' }}</td>
                <td class="text-center">{{ formatDate(r.fecha) }}</td>
                <td class="text-center">{{ r.hora || '—' }}</td>
                <td>
                  <div>{{ r.tipo_viaje || '—' }}</div>
                  <div v-if="r.hotel" class="text-muted small">{{ r.hotel }}</div>
                </td>
                <td>{{ r.pasajero || '—' }}</td>
                <td class="text-center">{{ r.vehiculo || '—' }}</td>
                <td class="text-center text-nowrap">{{ r.precio != null ? `US$ ${r.precio.toFixed(2)}` : '—' }}</td>
                <td class="text-center">{{ r.ciudad || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="table-footer d-flex justify-content-end align-items-center">
          <pagination
            v-if="filteredRows.length"
            :total="filteredRows.length"
            :page="page"
            :page-size="pageSize"
            :pages="pages"
            :changePage="changePage"
            :changePageSize="changePageSize"
          />
          <div class="table-no-results" v-else>
            <p class="m-0 px-3">No hay filas con ese estado.</p>
          </div>
        </div>
      </div>
    </template>

    <!-- ═══════════ 3. RESULTADO ═══════════ -->
    <b-card v-if="step === 'result' && result" tag="article" no-body class="custom-card" style="max-width: 760px">
      <b-card-header header-tag="section" class="custom-card-header">
        <h3>Importación terminada</h3>
      </b-card-header>
      <b-card-body class="custom-card-body">
        <div class="result-line result-line--ok">
          <i class="ri-checkbox-circle-line"></i>
          <span>
            <strong>{{ result.creadas }}</strong> viaje{{ result.creadas === 1 ? '' : 's' }}
            importado{{ result.creadas === 1 ? '' : 's' }} correctamente
          </span>
        </div>
        <div v-if="createdByCity.length" class="result-cities">
          <span v-for="c in createdByCity" :key="c.city" class="mr-3">{{ c.city }}: {{ c.count }}</span>
        </div>
        <div v-if="result.omitidas" class="result-line">
          <i class="ri-indeterminate-circle-line"></i>
          <span><strong>{{ result.omitidas }}</strong> omitido{{ result.omitidas === 1 ? '' : 's' }} (ya existían)</span>
        </div>
        <div v-if="result.errores" class="result-line result-line--error">
          <i class="ri-close-circle-line"></i>
          <span><strong>{{ result.errores }}</strong> no se pudieron importar</span>
        </div>
        <ul v-if="result.detalle_errores && result.detalle_errores.length" class="result-errors">
          <li v-for="e in result.detalle_errores" :key="e.ride_code">
            <span class="ride-code">{{ e.ride_code }}</span> — {{ e.motivo }}
          </li>
        </ul>

        <p class="text-muted small mt-3 mb-0">
          Para ver los viajes importados, elige la ciudad en el selector del encabezado y entra a Viajes.
          La lista muestra una ciudad a la vez.
        </p>

        <div class="d-flex justify-content-end mt-4">
          <router-link :to="{ name: 'imports-despegar' }" class="button button-primary">Volver al historial</router-link>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { Alert, Pagination } from "wize-admin";

import { ImportService } from "@/core/services";

// Orden de la tabla del preview: primero lo que hay que revisar (DEF-ADM-022 §5.3).
const STATUS_ORDER = { ERROR: 0, NUEVA: 1, DUPLICADA: 2 };

const STATUS_META = {
  NUEVA: { label: "Nueva", color: "badge-green" },
  DUPLICADA: { label: "Duplicada", color: "badge-gray" },
  ERROR: { label: "Error", color: "badge-red" },
};

// Importador de viajes desde el Excel de Despegar (DEF-ADM-022 §5): subir el
// archivo, revisar el preview y confirmar.
export default {
  name: "DespegarUpload",
  components: { Pagination },
  data() {
    return {
      step: "upload", // upload | preview | result
      file: null,
      dragOver: false,
      preview: null,
      result: null,
      ignoreErrors: false,
      statusFilter: "ALL",
      page: 1,
      pageSize: 100, // mismo valor inicial que el selector del componente pagination
    };
  },
  computed: {
    fileSize() {
      if (!this.file) return "";
      const kb = this.file.size / 1024;
      return kb < 1024 ? `${kb.toFixed(0)} KB` : `${(kb / 1024).toFixed(1)} MB`;
    },
    summaryCards() {
      const p = this.preview;
      return [
        { key: "nuevas", label: "Nuevas", value: p.nuevas },
        { key: "duplicadas", label: "Duplicadas", value: p.duplicadas },
        { key: "errores", label: "Errores", value: p.errores },
        { key: "total", label: "Total", value: p.total },
      ];
    },
    sortedRows() {
      return [...this.preview.filas].sort(
        (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.row - b.row
      );
    },
    statusFilters() {
      const p = this.preview;
      return [
        { key: "ALL", label: "Todas", count: p.total },
        { key: "ERROR", label: "Errores", count: p.errores },
        { key: "NUEVA", label: "Nuevas", count: p.nuevas },
        { key: "DUPLICADA", label: "Duplicadas", count: p.duplicadas },
      ];
    },
    filteredRows() {
      if (this.statusFilter === "ALL") return this.sortedRows;
      return this.sortedRows.filter((r) => r.status === this.statusFilter);
    },
    pages() {
      return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize));
    },
    pagedRows() {
      const start = (this.page - 1) * this.pageSize;
      return this.filteredRows.slice(start, start + this.pageSize);
    },
    newRideCodes() {
      return this.preview ? this.preview.filas.filter((r) => r.status === "NUEVA").map((r) => r.ride_code) : [];
    },
    // Con errores se bloquea salvo que el operador marque "importar aunque haya
    // errores": igual solo se importan las NUEVA.
    canImport() {
      if (!this.preview || !this.preview.nuevas) return false;
      return !this.preview.errores || this.ignoreErrors;
    },
    createdByCity() {
      if (!this.result || !this.preview) return [];
      // Solo las NUEVA: una fila repetida en el archivo trae el mismo código
      // sin ciudad y pisaría a la original.
      const cityOf = {};
      this.preview.filas
        .filter((r) => r.status === "NUEVA")
        .forEach((r) => {
          cityOf[r.ride_code] = r.ciudad;
        });
      const counts = {};
      this.result.rides_creados.forEach((code) => {
        const city = cityOf[code] || "Sin ciudad";
        counts[city] = (counts[city] || 0) + 1;
      });
      return Object.keys(counts).map((city) => ({ city, count: counts[city] }));
    },
  },
  methods: {
    statusMeta(status) {
      return STATUS_META[status] || { label: status, color: "badge-gray" };
    },
    // Duplicada en la BD (tiene booking_id) o repetida dentro del mismo Excel.
    duplicateReason(r) {
      if (r.booking_id) return "Ya está en viajes";
      return r.ride_existente ? `Repetida: ${r.ride_existente}` : "Duplicada";
    },
    formatDate(value) {
      if (!value) return "—";
      const [y, m, d] = value.split("-");
      return `${d}/${m}/${y}`;
    },
    onFileChange(ev) {
      this.pickFile(ev.target.files[0]);
      ev.target.value = "";
    },
    onDrop(ev) {
      this.dragOver = false;
      this.pickFile(ev.dataTransfer.files[0]);
    },
    pickFile(file) {
      if (!file) return;
      if (!file.name.toLowerCase().endsWith(".xlsx")) {
        Alert.error("El archivo tiene que ser un Excel .xlsx");
        return;
      }
      this.file = file;
    },
    async processFile() {
      try {
        await this.$store.dispatch("app/loading", true);
        const res = await ImportService.previewDespegar(this.file);
        this.preview = res.payload;
        this.ignoreErrors = false;
        this.statusFilter = "ALL";
        this.page = 1;
        this.step = "preview";
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudo procesar el archivo");
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    async confirmImport() {
      try {
        await this.$store.dispatch("app/loading", true);
        const res = await ImportService.confirmDespegar(this.newRideCodes);
        this.result = res.payload;
        this.step = "result";
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors ? ex.errors.message : "No se pudieron importar los viajes");
      } finally {
        await this.$store.dispatch("app/loading", false);
      }
    },
    setStatusFilter(key) {
      this.statusFilter = key;
      this.page = 1;
    },
    changePage(p) {
      this.page = p;
    },
    changePageSize(size) {
      this.pageSize = size;
      this.page = 1;
    },
    reset() {
      this.step = "upload";
      this.file = null;
      this.preview = null;
      this.result = null;
      this.ignoreErrors = false;
    },
  },
};
</script>

<style lang="stylus" scoped>
.drop-zone
  border 2px dashed #ced4da
  border-radius 10px
  padding 36px 16px
  text-align center
  cursor pointer
  transition border-color .2s, background-color .2s
  &:hover, &--over
    border-color #007bff
    background rgba(0,123,255,.04)
  &--ready
    border-style solid
    border-color #28a745
    background rgba(40,167,69,.04)
.drop-zone__icon
  font-size 40px
  color #28a745
.drop-zone__name
  font-weight 600
  margin-top 6px
.drop-zone__hint
  font-size 13px
  color #6c757d

// Cards con el mismo formato que las de Gastos de flota.
.summary-card
  background #fff
  border 1px solid #e5e7eb
  border-radius 10px
  padding 14px 16px
  display flex
  flex-direction column
  height 100%
  &.nuevas
    border-left 4px solid #28a745
  &.duplicadas
    border-left 4px solid #8592A6
  &.errores
    border-left 4px solid #dc3545
  &.total
    border-left 4px solid #2c3e50
.summary-card__label
  font-size 11px
  text-transform uppercase
  letter-spacing .05em
  color #8592A6
.summary-card__value
  font-size 20px
  font-weight 700
  margin-top 4px

// Mismo pill que el tarifario (Tarifas → Precios base).
.status-pill
  border 1px solid #ced4da
  background #fff
  border-radius 999px
  padding 6px 14px
  font-size 13px
  color #6c757d
  cursor pointer
  &.active
    background rgba(0,123,255,.1)
    border-color #007bff
    color #007bff
    font-weight 600

.ignore-errors
  display inline-flex
  align-items center
  gap 6px
  font-size 13px
  cursor pointer

.status-cell
  min-width 190px
.row-messages
  list-style none
  padding 0
  margin 4px 0 0
  font-size 12px
  color #6c757d
  &--error
    color #dc3545
  &--warning
    color #b26a00
.row-error td
  background #fff8f8
.row-duplicada td
  color #8592A6
.ride-code
  font-family monospace
  font-size 12px
  white-space nowrap

.result-line
  display flex
  align-items center
  gap 8px
  font-size 15px
  margin-bottom 8px
  i
    font-size 20px
    color #8592A6
  &--ok i
    color #28a745
  &--error i
    color #dc3545
.result-cities
  margin 0 0 10px 28px
  font-size 13px
  color #6c757d
.result-errors
  font-size 13px
  color #dc3545
  padding-left 28px
</style>
